import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  OnChanges,
  OnInit,
  input,
  signal,
  effect,
  AfterViewInit,
  OnDestroy,
  inject
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Table, TableModule } from 'primeng/table';
import { TranscriptMetadata } from '../../../../models/documentResponse';
import { TranscriptType, TranscriptTypeLabels } from '../../../../constants';
import { FronteggAuthService } from '@frontegg/angular';
import { ItpExtractionDataService } from '../../../../services/itp-extraction/itp-extraction-data.service';
import { ItpExtractionValidationService } from '../../../../services/itp-extraction/itp-extraction-validation.service';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import {
  TranscriptExtractionData,
  FieldWithBbox,
  RequiredFields
} from '../../../../models/transcriptExtractionData.model';
import { Bind } from 'primeng/bind';
import { PrimeTemplate, SortMeta, FilterOperator, FilterService, SelectItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { Divider } from 'primeng/divider';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { NgClass, DatePipe } from '@angular/common';
import { Chip } from 'primeng/chip';
import { Dialog } from 'primeng/dialog';
import { ItpExtractionConfidenceComponent } from '../itp-extraction-confidence/itp-extraction-confidence.component';

interface Column {
  field: string;
  header: string;
  resizable?: boolean;
  sortable?: boolean;
  filterType?: 'text' | 'date';
  width?: string;
  visible?: () => boolean;
}
@Component({
  selector: 'app-transcript-table',
  templateUrl: './transcript-table.component.html',
  styleUrls: ['./transcript-table.component.scss'],
  imports: [
    Bind,
    TableModule,
    PrimeTemplate,
    Button,
    Tooltip,
    Tag,
    Divider,
    IconField,
    InputIcon,
    InputText,
    NgClass,
    Chip,
    Dialog,
    ItpExtractionConfidenceComponent,
    DatePipe,
    TranslatePipe
  ]
})
export class TranscriptTableComponent implements AfterViewInit, OnDestroy {
  private translateService = inject(TranslateService);
  private fronteggAuthService = inject(FronteggAuthService);
  private itpExtractionDataService = inject(ItpExtractionDataService);
  private itpExtractionValidationService = inject(ItpExtractionValidationService);
  private featureFlagService = inject(FeatureFlagService);
  private filterService = inject(FilterService);

  documents = input<TranscriptMetadata[]>([]);
  isLoading = input<boolean>(false);
  headerLabelKey = input<string>('ITP.transcriptQueue');
  activeTypeFilter = input<TranscriptType | null>(null);
  isItpPowerProcessor = input<boolean>(false);

  readonly transcriptType = TranscriptType;

  /**
   * Gets the display label for a transcript type id
   * @param typeId - The transcript type id
   */
  getTypeLabel(typeId: TranscriptType): string {
    return TranscriptTypeLabels[typeId] ?? '';
  }
  trackByTranscriptId = (index: number, document: TranscriptMetadata): string => {
    return document.transId;
  };
  @Input() isAdmin: boolean = false;

  @Output() documentSelected = new EventEmitter<TranscriptMetadata>();
  @Output() documentDeleted = new EventEmitter<TranscriptMetadata>();
  @Output() documentsAssigned = new EventEmitter<TranscriptMetadata[]>();
  @Output() documentsSendToSis = new EventEmitter<TranscriptMetadata[]>();
  @Output() documentsUnassign = new EventEmitter<TranscriptMetadata[]>();
  @Output() filtersCleared = new EventEmitter<void>();
  @Output() typeFilterChanged = new EventEmitter<TranscriptType>();

  @ViewChild('dt') dt!: Table;
  @ViewChild('globalSearch') globalSearch!: ElementRef<HTMLInputElement>;

  itpTypeFilterFlag = toSignal(
    this.featureFlagService.getFlag('feature-itp-high-school-transcripts', false)
  );

  reviewIndicatorFlag = toSignal(
    this.featureFlagService.getFlag('feature-itp-review-indicators', false)
  );

  selectedDocuments: TranscriptMetadata[] = [];
  documentToDelete: TranscriptMetadata | null = null;
  activeTableSize = false;
  readonly stateKey = 'transcript-queue-table-v3';
  readonly defaultSort: SortMeta[] = [{ field: 'createdAt', order: 1 }];
  readonly initialSort: SortMeta[] = this.loadSavedSort() ?? [...this.defaultSort];
  deleteConfirmationVisible: boolean = false;
  sendToSISConfirmationVisible: boolean = false;

  // Confidence modal properties
  confidenceModalVisible = false;
  selectedConfidenceScore: number | null = null;
  selectedConfidenceExplanation: string | null = null;
  selectedFieldsNeedingAttention = 0;
  isLoadingConfidenceData = false;

  searchTerm = signal<string>(sessionStorage.getItem('transcriptSearch') || '');
  private readonly searchEffect = effect(() => {
    const search = this.searchTerm();
    search
      ? sessionStorage.setItem('transcriptSearch', search)
      : sessionStorage.removeItem('transcriptSearch');
  });

  private readonly documentsEffect = effect(() => {
    const docs = this.documents();
    if (this.selectedDocuments.length === 0) return;
    const docsMap = new Map(docs.map(d => [d.transId, d]));
    this.selectedDocuments = this.selectedDocuments
      .filter(d => docsMap.has(d.transId))
      .map(d => docsMap.get(d.transId)!);
  });

  private readonly dateFiltersRegistered = this.registerDateFilters();

  private searchSubject = new Subject<string>();
  private searchSubscription = this.searchSubject.pipe(debounceTime(300)).subscribe(value => {
    this.dt.filterGlobal(value, 'contains');
    this.selectedDocuments = [];
  });

  cols: Column[] = [
    {
      field: 'fileName',
      header: 'ITP.transcriptName',
      sortable: true,
      filterType: 'text',
      width: '250px'
    },
    {
      field: 'student_name',
      header: 'ITP.studentName',
      sortable: true,
      filterType: 'text',
      width: '180px'
    },
    {
      field: 'extractionStatus',
      header: 'ITP.transcriptStatus',
      sortable: true,
      filterType: 'text'
    },
    {
      field: 'typeId',
      header: 'ITP.type',
      sortable: true,
      visible: () => this.itpTypeFilterFlag()
    },
    {
      field: 'lockedByUser',
      header: 'ITP.assignedTo',
      sortable: true,
      filterType: 'text'
    },
    {
      field: 'createdAt',
      header: 'ITP.uploadDate',
      sortable: true,
      filterType: 'date'
    }
  ];

  readonly dateMatchModes: SelectItem[] = [
    { label: 'Date is', value: 'createdOn' },
    { label: 'Date is before', value: 'createdBefore' },
    { label: 'Date is after', value: 'createdAfter' }
  ];

  /**
   * Can be removed when itpTypeFilterFlag is removed
   * Gets the columns that should currently be visible in the table
   */
  get visibleCols(): Column[] {
    return this.cols.filter(col => !col.visible || col.visible());
  }

  /**
   * After view initialization, apply saved global search filter if available.
   */
  ngAfterViewInit(): void {
    const savedSearch = this.searchTerm();
    if (savedSearch && this.globalSearch) {
      this.globalSearch.nativeElement.value = savedSearch;
      this.dt.filterGlobal(savedSearch, 'contains');
    }
  }

  /**
   * Cleans up subscriptions on component destroy
   */
  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  /**
   * Reads the sort that p-table saved to session storage
   * @returns The saved sort, or null if none is saved
   */
  private loadSavedSort(): SortMeta[] | null {
    try {
      const saved = sessionStorage.getItem(this.stateKey);
      const sort = saved ? JSON.parse(saved).multiSortMeta : null;
      return Array.isArray(sort) && sort.length > 0 ? sort : null;
    } catch {
      return null;
    }
  }

  /**
   * Registers date comparison filters that coerce the ISO string value to a Date
   */
  private registerDateFilters(): true {
    const day = (v: string | Date): number => {
      const d = new Date(v);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };
    this.filterService.register('createdOn', (v: string, f: Date) =>
      !f ? true : !!v && day(v) === day(f)
    );
    this.filterService.register('createdBefore', (v: string, f: Date) =>
      !f ? true : !!v && day(v) < day(f)
    );
    this.filterService.register('createdAfter', (v: string, f: Date) =>
      !f ? true : !!v && day(v) > day(f)
    );
    return true;
  }

  /**
   * Determines if a row is selectable
   * @param data - Row context provided by PrimeNG
   * @param data.data - The transcript metadata for the row being evaluated
   */
  isRowSelectable = ({ data }: { data: TranscriptMetadata }): boolean =>
    !this.isLockedByOtherUser(data) || this.isITPAdmin;

  /**
   * Gets the current user from Frontegg auth service
   */
  private get currentUser() {
    return this.fronteggAuthService.user;
  }

  /**
   * Gets the current user's display name
   */
  private get currentUserDisplayName(): string {
    return (this.currentUser as any)?.customClaims?.display_name || this.currentUser?.name || '';
  }

  /**
   * Sets the table size. The default is large. False = large, True = small.
   * @param size - The size determined by the button pressed.
   */
  setTableSize(size: 'small' | 'large') {
    this.activeTableSize = size === 'small';
  }

  /**
   * Gets the current table size
   */
  get tableSize(): 'small' | 'large' {
    return this.activeTableSize ? 'small' : 'large';
  }

  /**
   * Handles global filtering of the table based on user input
   * @param event - The input event from the global filter field
   */
  onGlobalFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    this.searchSubject.next(value);
  }

  /**
   * Handles the filter type change when clicking on the filter chips
   * @param type - The transcript type to filter by
   */
  onFilterByType(type: TranscriptType): void {
    this.typeFilterChanged.emit(type);
  }

  /**
   * Blanks every column filter but keeps the global filter, preserving the structure
   * p-columnFilter relies on
   */
  resetColumnFilters(): void {
    Object.keys(this.dt.filters)
      .filter(field => field !== 'global')
      .forEach(field => {
        this.dt.filters[field] = [
          { value: null, matchMode: 'startsWith', operator: FilterOperator.AND }
        ];
      });
    this.dt._filter();
  }

  /**
   * Clears all filters applied to the table
   */
  clearAllFilters(): void {
    this.dt.clear();
    this.dt.multiSortMeta = [...this.defaultSort];
    this.dt.sortMultiple();
    this.selectedDocuments = [];
    this.searchTerm.set('');

    if (this.globalSearch) {
      this.globalSearch.nativeElement.value = '';
    }

    this.filtersCleared.emit();
  }

  /**
   * Determines if a document should be disabled based on its extraction status
   * @param document - The transcript metadata to check
   * @returns True if document should be disabled, false otherwise
   */
  disableDocuments(document: TranscriptMetadata): boolean {
    return (
      document.extractionStatus === 'Pending Extraction' ||
      document.extractionStatus === 'Failed' ||
      document.extractionStatus === 'Extracting'
    );
  }

  /**
   * Gets the count of selected documents that are not locked by a user
   */
  get selectableDocumentsCount(): number {
    return this.selectedDocuments.length;
  }

  /**
   * Handles the document open event when a user clicks to view a transcript.
   * @param event - The click event triggered by the user.
   * @param document - The transcript metadata to open.
   */
  openDocument(event: Event, document: TranscriptMetadata): void {
    event.preventDefault();
    if (!this.disableDocuments(document)) {
      this.documentSelected.emit(document);
    }
  }

  /**
   * Shows the delete confirmation dialog
   * @param document - Optional single document to delete, otherwise uses selected documents
   */
  confirmDeleteDialog(document?: TranscriptMetadata) {
    this.documentToDelete = document ?? null;
    this.deleteConfirmationVisible = true;
  }

  /**
   * Shows the send to sis confirmation dialog
   */
  confirmSendToSisDialog() {
    this.sendToSISConfirmationVisible = true;
  }
  /**
   * Handles the document deletion after confirmation
   */
  onDeleteDocument() {
    if (this.documentToDelete) {
      this.documentDeleted.emit(this.documentToDelete);
      this.documentToDelete = null;
    } else {
      this.selectedDocuments.forEach(doc => {
        this.documentDeleted.emit(doc);
      });
      this.selectedDocuments = [];
    }
    this.deleteConfirmationVisible = false;
  }

  /**
   * Gets the value of a specific field from a document
   * @param document - The document object
   * @param field - The field name to retrieve
   * @returns The value of the specified field
   */
  getFieldValue(document: any, field: string): any {
    return document[field];
  }

  /**
   * Session state storage also saves widths of columns, so we need to ignore them to have the table width fill and resize.
   * @param table - The p-table instance that restored its state
   */
  ignoreSavedColumnWidths(table: Table): void {
    table.columnWidthsState = undefined;
    table.tableWidthState = undefined;
  }

  /**
   * Gets the CSS class name for a given status
   * @param status - The status string
   * @returns The corresponding CSS class name
   */
  getStatusClass(status: string): string {
    switch (status) {
      case 'Pending Extraction':
        return 'status-pending-extraction';
      case 'Extracting':
        return 'status-extracting';
      case 'Ready for Export':
        return 'status-ready-for-export';
      case 'Needs Review':
        return 'status-needs-review';
      case 'Failed':
        return 'status-failed';
      case 'Assigned':
        return 'status-assigned';
      case 'Exported':
        return 'status-exported';
      case 'Archived':
        return 'status-archived';
      case 'Failed to Export':
        return 'status-failed';
      case 'Failed Matching':
        return 'status-failed';
      case 'Partially Exported':
      case 'Partial Export Failure':
        return 'status-needs-review';
      default:
        return 'status-default';
    }
  }

  /**
   * Gets the translation variable key for a given status
   * @param status - The status string
   * @returns The corresponding translation variable key
   */
  getStatusTranslation(status: string): string {
    switch (status) {
      case 'Pending Extraction':
        return 'transcriptData.pendingExtractionStatus';
      case 'Extracting':
        return 'transcriptData.extractingStatus';
      case 'Ready for Export':
        return 'transcriptData.readyForExportStatus';
      case 'Needs Review':
        return 'transcriptData.needsReviewStatus';
      case 'Failed':
        return 'transcriptData.failedStatus';
      case 'Assigned':
        return 'transcriptData.assignedStatus';
      case 'Exported':
        return 'transcriptData.exportedStatus';
      case 'Archived':
        return 'transcriptData.archivedStatus';
      case 'Failed to Export':
        return 'transcriptData.failedToExport';
      case 'Failed Matching':
        return 'transcriptData.failedMatching';
      case 'Partially Exported':
      case 'Partial Export Failure':
        return 'transcriptData.partiallyExported';
      default:
        return 'transcriptData.defaultStatus';
    }
  }

  /**
   * Checks if assignment is available
   */
  get canAssign(): boolean {
    if (!this.selectedDocuments || this.selectedDocuments.length === 0) {
      return false;
    }

    const allUnassign = this.selectedDocuments.every(doc => !doc.lockedByUser?.trim());

    const allAssigned = this.selectedDocuments.every(doc => !!doc.lockedByUser?.trim());

    if (allAssigned) {
      return false;
    }

    return allUnassign;
  }

  /**
   * Checks if Send to SIS is available
   */
  get canSendToSIS(): boolean {
    if (!this.selectedDocuments || this.selectedDocuments.length === 0) {
      return false;
    }

    const anyAssignedToOthers = this.selectedDocuments.some(doc => {
      const lockedBy = doc.lockedByUser?.trim();
      return lockedBy && lockedBy !== this.currentUserDisplayName;
    });

    if (anyAssignedToOthers) {
      return false;
    }

    const allowedStatuses = ['Ready for Export', 'Failed to Export', 'Failed Matching'];

    return this.selectedDocuments.every(doc =>
      allowedStatuses.includes(doc.extractionStatus ?? '')
    );
  }

  /**
   * Checks if can unassign row
   * @returns True if the row can be unassigned, false otherwise
   */
  get canUnassign(): boolean {
    if (!this.selectedDocuments || this.selectedDocuments.length === 0) {
      return false;
    }

    const allAssigned = this.selectedDocuments.every(doc => !!doc.lockedByUser?.trim());

    if (!allAssigned) {
      return false;
    }

    if (this.isITPAdmin) {
      return true;
    }

    return this.selectedDocuments.every(
      doc => doc.lockedByUser?.trim() === this.currentUserDisplayName
    );
  }

  /**
   * Enables deletion based on user role and document assignment status.
   */
  get canDelete(): boolean {
    const assignedToOthers = this.selectedDocuments.filter(doc => {
      const lockedBy = doc.lockedByUser?.trim();
      return lockedBy && lockedBy !== this.currentUserDisplayName;
    });

    const isExtracting = this.selectedDocuments.some(
      doc => doc.extractionStatus === 'Pending Extraction' || doc.extractionStatus === 'Extracting'
    );

    if (this.isITPAdmin) {
      return !isExtracting;
    }

    return assignedToOthers.length === 0 && !isExtracting;
  }

  /**
   * Check if user is ITP Admin
   */
  get isITPAdmin(): boolean {
    return !!this.currentUser?.roles?.some((role: any) => role.key === 'ITP Admin');
  }

  /**
   * Checks if a document is locked by a different user
   * @param document - The transcript metadata to check
   * @returns True if locked by another user, false if unlocked or locked by current user
   */
  isLockedByOtherUser(document: TranscriptMetadata): boolean {
    const lockedBy = document.lockedByUser?.trim();
    if (!lockedBy) {
      return false;
    }

    return lockedBy !== this.currentUserDisplayName;
  }
  /**
   * Handles assignment action
   */
  onAssign(): void {
    if (this.canAssign) {
      this.documentsAssigned.emit(this.selectedDocuments);
      this.selectedDocuments = [];
    }
  }
  /**
   * Handles Unassign action
   */
  onUnassign(): void {
    if (this.canUnassign) {
      this.documentsUnassign.emit(this.selectedDocuments);
      this.selectedDocuments = [];
    }
  }
  /**
   * Handles send to sis action
   */
  onSendToSis(): void {
    if (this.canSendToSIS) {
      this.sendToSISConfirmationVisible = false;
      this.documentsSendToSis.emit(this.selectedDocuments);
      this.selectedDocuments = [];
    }
  }

  /**
   * Shows the confidence modal for a document's confidence score
   * Loads full transcript data to calculate fields needing attention
   * @param event - The click event
   * @param document - The transcript document
   */
  async showConfidenceModal(event: Event, document: TranscriptMetadata): Promise<void> {
    event.stopPropagation();

    const confidenceScore = this.getConfidenceScore(document);
    if (confidenceScore === null || confidenceScore === undefined) {
      return;
    }

    this.selectedConfidenceScore = confidenceScore;
    this.selectedConfidenceExplanation = null;
    this.selectedFieldsNeedingAttention = 0;
    this.confidenceModalVisible = true;
    this.isLoadingConfidenceData = true;

    try {
      const [transcriptResult, requiredFields] = await Promise.all([
        this.itpExtractionDataService.loadTranscriptData(document.transId),
        this.itpExtractionDataService.loadRequiredFields(document.typeId ?? TranscriptType.College)
      ]);

      if (transcriptResult.data) {
        this.selectedConfidenceExplanation =
          transcriptResult.data.Metadata?.ConfidenceLevelReasoning || null;

        this.selectedFieldsNeedingAttention =
          this.itpExtractionValidationService.calculateFieldsNeedingAttention(
            transcriptResult.data,
            requiredFields
          );
      }
    } catch (error) {
      console.error('Failed to load confidence data:', error);
    } finally {
      this.isLoadingConfidenceData = false;
    }
  }

  /**
   * Handles closing of the confidence modal and resets related state
   */
  onConfidenceModalClose(): void {
    this.confidenceModalVisible = false;
    this.selectedConfidenceScore = null;
    this.selectedConfidenceExplanation = null;
    this.selectedFieldsNeedingAttention = 0;
  }

  /**
   * Gets the numeric confidence score from a document
   * @param document - The transcript metadata document
   * @returns The numeric confidence score (0-1 range)
   */
  getConfidenceScore(document: TranscriptMetadata): number | null {
    if (document.confidenceScore !== null && document.confidenceScore !== undefined) {
      return document.confidenceScore;
    }
    if (document.confidence_score) {
      const parsed = parseFloat(document.confidence_score.replace('%', ''));
      return isNaN(parsed) ? null : parsed / 100;
    }
    return null;
  }
}
